import { Link } from '@tanstack/react-router';
import { TFunction } from 'i18next';
import { type SystemWithCapacity } from '@bliks-io/contracts';
import {
  cn,
  createColumnHeader,
  renderFallback,
  createActionsColumn,
  createUserCardColumn,
  type TombstoneLookup,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { CapacityCell } from '@/components/capacity-cell';

/**
 * Factory function to create the Logo column for system tables
 * Note: Does NOT show fallback letters when logo is missing (empty cell instead)
 */
export function createSystemLogoColumn(t: TFunction): DataTableColumnDef<SystemWithCapacity> {
  return {
    id: 'logo',
    meta: { label: t('common.Logo') },
    header: createColumnHeader(t('common.Logo')),
    cell: ({ row }) => {
      const system = row.original;
      let logoUrl: string | undefined;

      if (system.logo) {
        if (system.logo.type === 'svg') {
          logoUrl = system.logo.svg;
        } else {
          logoUrl = system.logo.thumbnail?.webp || system.logo.thumbnail?.png;
        }
      }

      // Only render image if there's a logo URL (no fallback = empty cell)
      if (!logoUrl) {
        return null;
      }

      return (
        <div className="flex items-center justify-center h-[60px] w-[60px]">
          <img
            src={logoUrl}
            alt={system.name}
            className="max-h-[60px] max-w-[60px] h-auto w-auto object-contain"
          />
        </div>
      );
    },
    size: 60
  };
}

/**
 * Factory function to create the Name column for system tables
 */
export function createSystemNameColumn(
  slug: string,
  t: TFunction
): DataTableColumnDef<SystemWithCapacity> {
  return {
    accessorKey: 'name',
    meta: { label: t('organization.systems.softwareSystem'), filterType: 'text' },
    header: createColumnHeader(t('organization.systems.softwareSystem')),
    cell: ({ row }) => (
      <Link
        to="/$slug/systems/$systemId"
        params={{ slug, systemId: row.original._id }}
        className="font-medium hover:underline"
      >
        {row.getValue('name')}
      </Link>
    ),
    enableColumnFilter: true
  };
}

/**
 * Factory function to create the System Owner column for system tables
 * Uses unified UserCard component with data populated from backend
 */
export function createSystemOwnerColumn(
  t: TFunction,
  userLookup?: TombstoneLookup
): DataTableColumnDef<SystemWithCapacity> {
  return createUserCardColumn<SystemWithCapacity>(
    'systemOwner',
    t('organization.systems.systemOwner'),
    {
      variant: 'with-role',
      itemVariant: 'outline',
      size: 'sm',
      enableFiltering: true,
      userLookup
    }
  );
}

/**
 * Factory function to create the Category column for system tables
 */
export function createSystemCategoryColumn(t: TFunction): DataTableColumnDef<SystemWithCapacity> {
  return {
    accessorKey: 'category',
    meta: { label: t('organization.systems.category'), filterType: 'text' },
    header: createColumnHeader(t('organization.systems.category')),
    cell: ({ row }) => renderFallback(row.getValue('category')),
    enableColumnFilter: true
  };
}

/**
 * Factory function to create the Capacity Binding column for system tables
 */
export function createSystemCapacityColumn(t: TFunction): DataTableColumnDef<SystemWithCapacity> {
  return {
    id: 'capacityBinding',
    accessorFn: (row) => row.capacityBinding?.hoursPerYear ?? -1,
    meta: { label: t('organization.systems.stats.capacityBinding') },
    header: createColumnHeader(t('organization.systems.stats.capacityBinding')),
    cell: ({ row }) => <CapacityCell capacity={row.original.capacityBinding} />,
    enableColumnFilter: false,
    enableSorting: true,
    sortFn: (rowA, rowB) => {
      const a = rowA.original.capacityBinding?.hoursPerYear ?? -1;
      const b = rowB.original.capacityBinding?.hoursPerYear ?? -1;
      return a - b;
    }
  };
}

/**
 * Factory function to create the Licence Seats column for system tables
 */
export function createSystemLicenceSeatsColumn(
  t: TFunction
): DataTableColumnDef<SystemWithCapacity> {
  return {
    id: 'licenceSeats',
    accessorFn: (row) => row.licenceSeats ?? -1,
    meta: { label: t('organization.systems.stats.licenceSeats') },
    header: createColumnHeader(t('organization.systems.stats.licenceSeats')),
    cell: ({ row }) => {
      const { licenceSeats, totalUsers } = row.original;
      if (licenceSeats === undefined || licenceSeats <= 0) return null;
      if (totalUsers === undefined) {
        return <span className="text-sm font-medium whitespace-nowrap">{licenceSeats}</span>;
      }

      const isOverCapacity = totalUsers > licenceSeats;
      const pct = Math.min((totalUsers / licenceSeats) * 100, 100);

      return (
        <div className="flex items-center gap-2 min-w-[100px]">
          <span
            className={cn('text-sm font-medium whitespace-nowrap', isOverCapacity && 'text-danger')}
          >
            {totalUsers} / {licenceSeats}
          </span>
          <div className="h-2 w-16 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                isOverCapacity ? 'bg-danger-solid' : 'bg-info-solid'
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      );
    },
    enableColumnFilter: false,
    enableSorting: true
  };
}

/**
 * Configuration for system actions column
 */
export interface SystemActionsConfig {
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

/**
 * Handlers for system actions
 */
export interface SystemActionsHandlers {
  onView?: (system: SystemWithCapacity) => void;
  onEdit?: (system: SystemWithCapacity) => void;
  onDelete?: (id: string) => void;
}

/**
 * Factory function to create the Actions column for system tables
 * Always uses the standard EntityActions dropdown menu for consistency
 */
export function createSystemActionsColumn(
  config: SystemActionsConfig,
  handlers: SystemActionsHandlers
): DataTableColumnDef<SystemWithCapacity> {
  const { canView = false, canEdit = false, canDelete = false } = config;
  const { onView, onEdit, onDelete } = handlers;

  // Always use the standard createActionsColumn helper for consistency
  return createActionsColumn<SystemWithCapacity>({
    onEdit: canEdit && onEdit ? (system) => onEdit(system) : undefined,
    onDelete: canDelete && onDelete ? (system) => onDelete(system._id) : undefined,
    onView: canView && onView ? (system) => onView(system) : undefined
  });
}
